import { SelectionModel } from '@angular/cdk/collections';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { Account, Role, User, UserAssociation } from 'app/core/models';
import difference from 'lodash-es/difference';
import { Observable, of, zip } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { RoleService } from '../../roles-permissions/services/roles.service';
import {
  CustomizableFieldSaveModel,
  FieldDefCustomization,
  LockableFieldDef,
  LockableFieldEditMode,
  LockableFieldLockDialogResult,
  LockableFieldSaveModel,
  LockedField,
} from '../locked-fields.types';
import { LockedFieldService } from '../services/locked-fields.service';

type EditDialogData = {
  mode: LockableFieldEditMode;
  lockableFieldDef: LockableFieldDef;
  customizations?: FieldDefCustomization[];
  fieldsSelection?: LockableFieldDef[];
  isBulkEditing?: boolean;
};

@Component({
  selector: 'app-locked-fields-edit',
  templateUrl: './locked-fields-edit.component.html',
  styleUrls: ['./locked-fields-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RoleService, LockedFieldService],
})
export class LockedFieldsEditComponent extends OnDestroyMixin implements OnInit {
  init$: Observable<boolean>;
  users: User[];
  roles: Role[];
  usersSelection = new SelectionModel<number>(true, []);
  rolesSelection = new SelectionModel<number>(true, []);
  selectionMemento: { users: number[]; roles: number[] } = { users: [], roles: [] };
  ui = { isLoading: false };
  customizationModes = ['value_hidden', 'excel_pull_locked', 'excel_push_locked'];

  get mode(): LockableFieldEditMode {
    return this.data.mode ?? 'lock';
  }

  get lockableFieldDef(): LockableFieldDef {
    return this.data.lockableFieldDef;
  }

  get selection(): LockableFieldDef[] {
    return this.data.fieldsSelection;
  }

  get customizations(): FieldDefCustomization[] {
    return this.data.customizations;
  }

  get isBulkEditing(): boolean {
    return this.data.isBulkEditing;
  }

  get title(): string {
    switch (this.mode) {
      case 'lock':
        return 'Lock';
      case 'value_hidden':
        return 'Hide';
      case 'excel_pull_locked':
        return 'Excel Pull';
      case 'excel_push_locked':
        return 'Excel Push';
      default:
        return '';
    }
  }

  private get account(): Account {
    return this.accountService.getCurrentAccount();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditDialogData,
    private readonly matDialogRef: MatDialogRef<LockedFieldsEditComponent, LockableFieldLockDialogResult>,
    private readonly matSnackBarService: MatSnackBar,
    private readonly accountService: AccountService,
    private readonly roleService: RoleService,
    private readonly lockedFieldService: LockedFieldService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.init$ = zip(
      this.accountService.userAssociations$.pipe(
        map((users: UserAssociation[]) =>
          users.reduce((prev: User[], cur: UserAssociation) => {
            if ((cur.role as unknown as Role)?.name === 'admin') {
              return prev;
            }

            const aux = prev.find(({ id }) => id === cur.user?.id);
            if (!aux) {
              prev.push(cur.user);
            }
            return prev;
          }, []),
        ),
      ),
      this.roleService.getByAccountId(Number(this.account.id)),
    ).pipe(
      untilComponentDestroyed(this),
      switchMap(([users, roles]: [User[], Role[]]) => {
        this.users = users;
        this.roles = roles.filter(({ name }) => name !== 'admin');
        return of(true);
      }),
    );

    this.handleSelectionAndMemento();
  }

  save(): void {
    this.ui = { isLoading: true };

    const { name: fieldName, related_to_type: relatedToType } = this.lockableFieldDef;

    if (this.mode === 'lock') {
      const model: LockableFieldSaveModel = {
        accountId: this.account.id,
        fields: this.selection.length > 0 && this.isBulkEditing ? this.selection.map(({ name }) => name) : [fieldName],
        relatedToType,
        users: {
          toAdd: difference(this.usersSelection.selected, this.selectionMemento.users),
          toRemove: this.lockableFieldDef.lockInfo
            .filter(({ user_id }) =>
              difference(this.selectionMemento.users, this.usersSelection.selected).includes(user_id),
            )
            .map(({ id }) => id),
        },
        roles: {
          toAdd: difference(this.rolesSelection.selected, this.selectionMemento.roles),
          toRemove: this.lockableFieldDef.lockInfo
            .filter(({ role_id }) =>
              difference(this.selectionMemento.roles, this.rolesSelection.selected).includes(role_id),
            )
            .map(({ id }) => id),
        },
      };

      if (!this.hasAnyDifference(model)) {
        this.matDialogRef.close();
        return;
      }

      this.lockedFieldService
        .save(model)
        .pipe(untilComponentDestroyed(this))
        .subscribe((result: LockedField[][]) => {
          this.ui = { isLoading: false };

          this.matSnackBarService.open('Field lock options updated successfully', '', {
            duration: 2000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });

          const added = result.filter((item) => item);
          this.matDialogRef.close({
            added: added.map(([lockedField]) => lockedField),
            removed: [...model.users.toRemove, ...model.roles.toRemove],
          });
        });
    }

    if (this.customizationModes.includes(this.mode)) {
      const usersToAdd = difference(this.usersSelection.selected, this.selectionMemento.users);
      const usersToRemove = difference(this.selectionMemento.users, this.usersSelection.selected);
      const rolesToAdd = difference(this.rolesSelection.selected, this.selectionMemento.roles);
      const rolesToRemove = difference(this.selectionMemento.roles, this.rolesSelection.selected);

      const model: CustomizableFieldSaveModel = {
        fields:
          this.selection.length > 0 && this.isBulkEditing
            ? this.selection.map(({ name }) => name)
            : [this.lockableFieldDef.name],
        customization_name: this.mode,
        customization_value: true,
        field_def_related_to_type: this.lockableFieldDef.related_to_type,
        users: { toAdd: usersToAdd, toRemove: usersToRemove },
        roles: { toAdd: rolesToAdd, toRemove: rolesToRemove },
      };

      if (!this.hasAnyDifference(model)) {
        this.matDialogRef.close();
        return;
      }

      this.lockedFieldService
        .saveCustomizableFields(model)
        .pipe(untilComponentDestroyed(this))
        .subscribe((result) => {
          this.ui = { isLoading: false };

          this.matSnackBarService.open(this.title + ' options updated successfully', '', {
            duration: 2000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
          });
          this.matDialogRef.close({});
        });
    }
  }

  cancel(): void {
    this.matDialogRef.close();
  }

  private hasAnyDifference({ users, roles }: LockableFieldSaveModel | CustomizableFieldSaveModel): boolean {
    const changeCount = users.toAdd.length + users.toRemove.length + roles.toAdd.length + roles.toRemove.length;
    return changeCount > 0;
  }

  private handleSelectionAndMemento(): void {
    this.rolesSelection.clear();
    this.usersSelection.clear();

    const fieldDefs = this.selection?.length > 1 ? this.selection : [this.lockableFieldDef];

    const userIds = new Set<number>();
    const roleIds = new Set<number>();

    if (this.mode === 'lock') {
      fieldDefs.forEach((fd) => {
        (fd.lockInfo ?? [])
          .filter((l) => l.klass === fd.related_to_type)
          .forEach(({ user_id, role_id }) => {
            if (user_id) userIds.add(user_id);
            if (role_id) roleIds.add(role_id);
          });
      });
    } else {
      fieldDefs.forEach((fd) => {
        this.customizations
          .filter(
            (c) => c.field_def_related_to_type === fd.related_to_type && c.name === fd.name && c[this.mode] === true,
          )
          .forEach((c) => {
            if (c.related_to_type === 'User') {
              userIds.add(c.related_to_id);
            } else {
              roleIds.add(c.related_to_id);
            }
          });
      });
    }

    roleIds.forEach((id) => this.rolesSelection.select(id));
    userIds.forEach((id) => this.usersSelection.select(id));

    this.selectionMemento = {
      roles: [...this.rolesSelection.selected],
      users: [...this.usersSelection.selected],
    };
  }
}
